// NOTICE: KAPTION-INTEGRATION
// two-step-verification.UI.1 — one shared copy of the person's second-step
// status for the profile section, the admin banner and the AdminRequired screen.
//
// Gate (both required): the KAPTION_TWO_STEP_VERIFICATION flag through
// useKaptionFeatureFlags AND the `two_step_verification` feature permission
// through useFeaturePermissions. The server applies the same gate; when it says
// the feature is off (403) the status stays empty and nothing renders.
import { ref, computed } from 'vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import {
  hostOfOrigin,
  isPasskeyHostAllowed,
} from 'shared/helpers/kaptionWebauthn';

export const TWO_STEP_FLAG = 'KAPTION_TWO_STEP_VERIFICATION';
export const TWO_STEP_FEATURE = 'two_step_verification';
export const TWO_STEP_METHODS = ['passkey', 'whatsapp', 'authenticator'];

const status = ref(null);
const loading = ref(false);
let inflight = null;

export const resetKaptionTwoStepState = () => {
  status.value = null;
  loading.value = false;
  inflight = null;
};

export function useKaptionTwoStep() {
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const { canUseFeature } = useFeaturePermissions();

  const isEnabled = computed(
    () => isKaptionFlagEnabled(TWO_STEP_FLAG) && canUseFeature(TWO_STEP_FEATURE)
  );

  const setStatus = data => {
    status.value = data || null;
  };

  const fetchStatus = async ({ force = false } = {}) => {
    if (!isEnabled.value) return null;
    if (status.value && !force) return status.value;
    if (inflight) return inflight;

    loading.value = true;
    inflight = KaptionTwoStepAPI.getStatus()
      .then(({ data }) => {
        setStatus(data);
        return data;
      })
      .catch(() => {
        setStatus(null);
        return null;
      })
      .finally(() => {
        loading.value = false;
        inflight = null;
      });
    return inflight;
  };

  const isActive = computed(() => isEnabled.value && !!status.value?.active);
  const hasFactor = computed(() => !!status.value?.has_factor);
  const configuredMethods = computed(() =>
    TWO_STEP_METHODS.filter(
      method => status.value?.methods?.[method]?.configured
    )
  );
  const isRequired = computed(() => isActive.value && !!status.value?.required);
  const needsSetup = computed(() => isRequired.value && !hasFactor.value);
  const isBlocked = computed(() => needsSetup.value && !!status.value?.blocked);
  const graceEndsAt = computed(() =>
    status.value?.grace_ends_at ? new Date(status.value.grace_ends_at) : null
  );
  const daysLeft = computed(() => {
    if (!graceEndsAt.value) return null;
    const ms = graceEndsAt.value.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
  });
  // two-step-verification.PASSKEY.3 — passkeys can be added and used only on a
  // host inside the RP ID. The server already answers per request host; the
  // location.hostname check keeps the option hidden even if it did not.
  const passkeyAvailable = computed(() => {
    const passkey = status.value?.methods?.passkey;
    return !!passkey?.available && isPasskeyHostAllowed(passkey?.rp_id);
  });
  // Where passkeys work, when this host is not it ('' otherwise).
  const passkeyElsewhereHost = computed(() => {
    const passkey = status.value?.methods?.passkey;
    if (!passkey?.rp_id || passkeyAvailable.value) return '';
    return hostOfOrigin(passkey.origin) || passkey.rp_id;
  });
  const requiredAccountName = computed(
    () => status.value?.required_accounts?.[0]?.name || ''
  );

  return {
    status,
    loading,
    isEnabled,
    isActive,
    hasFactor,
    configuredMethods,
    isRequired,
    needsSetup,
    isBlocked,
    graceEndsAt,
    daysLeft,
    requiredAccountName,
    passkeyAvailable,
    passkeyElsewhereHost,
    fetchStatus,
    setStatus,
  };
}
